import { useQuery } from "@tanstack/react-query";
import { useApiWithLanguage } from "./useApiWithLanguage";

interface TournamentFilters {
  [key: string]: unknown;
}

const tournamentKeys = {
  all: ["tournaments"] as const,
  lists: () => [...tournamentKeys.all, "list"] as const,
  list: (filters?: TournamentFilters) => [...tournamentKeys.lists(), { filters }] as const,
  details: () => [...tournamentKeys.all, "detail"] as const,
  detail: (id: string) => [...tournamentKeys.details(), id] as const,
};

export function useTournaments(year?: number) {
  const { fetchWithLanguage } = useApiWithLanguage();

  const { data: tournaments, isLoading, error } = useQuery({
    queryKey: tournamentKeys.list({ year }),
    queryFn: async () => {
      const url =
        year !== undefined
          ? `/api/tournaments?year=${year}`
          : "/api/tournaments";
      const response = await fetchWithLanguage(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tournaments: ${response.statusText}`);
      }

      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
  });

  const tournamentYears = (tournaments?.tournamentYears as number[] | undefined) ?? [];

  return {
    tournaments: tournaments || [],
    tournamentYears,
    mostRecentYear: tournamentYears.length > 0 ? Math.max(...tournamentYears) : undefined,
    isLoading,
    error,
  };
}

export function useTournament(tournamentId: string) {
  const { fetchWithLanguage } = useApiWithLanguage();
  
  const { data: tournament, isLoading, error } = useQuery({
    queryKey: tournamentKeys.detail(tournamentId),
    queryFn: async () => {
      const response = await fetchWithLanguage(`/api/tournaments/${tournamentId}`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tournament: ${response.statusText}`);
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
    enabled: !!tournamentId,
  });

  return {
    tournament,
    isLoading,
    error,
  };
}